import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { analyticsEnabled, analyticsPage, AnalyticsConsent, CONSENT_KEY, readAnalyticsConsent, setAnalyticsConsent, updateAnalyticsAuth } from '../analytics';

export function AnalyticsPreferencesButton({ className = '' }: { className?: string }) {
  const { language } = useLanguage();
  const { user } = useAuth();
  if (!analyticsEnabled() || (user && user.role !== 'user')) return null;
  return <button type="button" className={className} onClick={() => window.dispatchEvent(new Event('gl:analytics-preferences'))}>
    {language === 'kk' ? 'Cookie баптаулары' : 'Настройки cookie'}
  </button>;
}

export default function Analytics() {
  const location = useLocation();
  const { user, loading } = useAuth();
  const { language } = useLanguage();
  const [consent, setConsent] = useState<AnalyticsConsent>(readAnalyticsConsent);
  const [preferences, setPreferences] = useState(false);
  const [eligible, setEligible] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  useEffect(() => {
    const changed = (event: Event) => setConsent((event as CustomEvent<AnalyticsConsent>).detail);
    const storage = (event: StorageEvent) => { if (event.key === CONSENT_KEY) setConsent(readAnalyticsConsent()); };
    const open = () => setPreferences(true);
    const support = (event: Event) => setSupportOpen(Boolean((event as CustomEvent<boolean>).detail));
    window.addEventListener('gl:analytics-consent', changed);
    window.addEventListener('gl:analytics-preferences', open);
    window.addEventListener('storage', storage);
    window.addEventListener('gl:support-visibility', support);
    return () => {
      window.removeEventListener('gl:analytics-consent', changed);
      window.removeEventListener('gl:analytics-preferences', open);
      window.removeEventListener('storage', storage);
      window.removeEventListener('gl:support-visibility', support);
    };
  }, []);
  useEffect(() => {
    setEligible(updateAnalyticsAuth(consent, user, loading, location.pathname + location.search) === 'allowed');
  }, [consent, user, loading, location.pathname, location.search]);
  // Opening support must not require an analytics choice. Restore this banner
  // on close without changing consent or covering the mobile chat composer.
  if (supportOpen || !analyticsEnabled() || !eligible || (!preferences && (consent !== null || !analyticsPage(location.pathname)))) return null;
  const choose = (choice: 'granted' | 'denied') => { setAnalyticsConsent(choice); setPreferences(false); };
  const kk = language === 'kk';
  return <section data-gl-cookie-banner aria-label={kk ? 'Аналитика cookie файлдары' : 'Аналитические cookie'}
    className="fixed inset-x-3 bottom-3 z-[250] mx-auto max-w-3xl rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700 shadow-xl sm:flex sm:items-center sm:gap-5">
    <p className="flex-1 leading-relaxed">
      {kk ? 'Рұқсатыңызбен Google Analytics және Яндекс Метрика сайттың қолданылуын өлшейді. Іздеу мәтіні мен профиль деректерін жібермейміз. Таңдауды cookie баптауларында өзгерте аласыз.' :
        'С вашего согласия Google Analytics и Яндекс Метрика измеряют использование сайта. Тексты поиска и данные профиля не отправляем. Выбор можно изменить в настройках cookie.'}{' '}
      <Link to="/privacy" className="underline underline-offset-2">{kk ? 'Құпиялық саясаты' : 'Политика конфиденциальности'}</Link>
    </p>
    <div className="mt-3 flex shrink-0 gap-2 sm:mt-0">
      <button type="button" className="rounded-xl border border-slate-300 px-4 py-2 font-semibold" onClick={() => choose('denied')}>
        {kk ? 'Бас тарту' : 'Отклонить'}</button>
      <button type="button" className="rounded-xl bg-goszakup-600 px-4 py-2 font-semibold text-white" onClick={() => choose('granted')}>
        {kk ? 'Рұқсат беру' : 'Разрешить'}</button>
    </div>
  </section>;
}
